/**
 * "HAVE WE CHASED *THIS* CUSTOMER?" — ASKED WITHOUT A DRAFT CARD IN FRONT OF YOU.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ⚠ WHY THIS EXISTS, AND WHAT THE REVIEW GOT RIGHT AND WRONG
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * A completeness review reported that the sent history "is reachable from exactly one place
 * — a draft card". HALF OF THAT IS WRONG AND THE OTHER HALF IS THE DEFECT.
 *
 * WRONG: `ViewToggle` renders unconditionally on this screen, in every view, so the
 * country-wide history has always been one click away whether or not a draft exists. The
 * "recently sent" strip offers a second route to the same place.
 *
 * RIGHT, AND IT IS THE PART THAT MATTERS: narrowing to A CUSTOMER was reachable ONLY from
 * `DraftCard`'s "What has been sent to X?" button. The moment you finish sending, that
 * customer has no draft and therefore no card — which is precisely when somebody asks
 * whether they were chased. The only remaining option was paging a whole country's list 25
 * rows at a time, and the party id could not even be looked up by hand because
 * `/countries/:cc/parties/:id` is not a mounted route.
 *
 * So this is the missing affordance and nothing more: type a name, pick the customer, the
 * list narrows. It reuses `usePartyLookup` — the SAME `GET /countries/:cc/parties?q=` search,
 * the same two-character minimum and the same `perPage: 8` cap the quote builder's picker
 * uses — rather than authoring a second search with subtly different rules.
 *
 * ⚠ IT SELECTS, IT NEVER CREATES. `NewInquiryPanel`'s picker can create a prospect inline
 * because an inquiry is often first contact. Nothing on a renewals HISTORY can be answered
 * by inventing a customer: a party that does not exist has, by construction, never been
 * emailed. Offering a create here would emit `party.created` from a read-only screen.
 *
 * ⚠ AND THE CHOSEN CUSTOMER IS NAMED ON SCREEN, NOT JUST IN THE URL. A narrowed list that
 * does not say it is narrowed gets read as the whole country's — and this list's entire
 * purpose is telling somebody whether a customer has been contacted, so a silent filter
 * produces exactly the wrong conclusion. Clearing it is one click, beside the name.
 */

import { useState } from 'react';
import { Search, X } from 'lucide-react';

import { usePartyLookup } from '../quotes/use-quotes';
import type { CountryCode } from '../../domain/countries';

export function CustomerFilter({
  countryCode,
  partyFilter,
  partyName,
  onSelect,
  onClear,
}: {
  countryCode: string;
  /** The party id currently narrowing the list, or null. Comes from the URL. */
  partyFilter: string | null;
  /**
   * The chosen customer's name IF this browser knows it — it is remembered from the click
   * that set the filter. `null` after a reload or a pasted link, where the id is all we have.
   *
   * ⚠ THE FALLBACK SAYS SO RATHER THAN SHOWING AN ID AS IF IT WERE A NAME. There is no route
   * that resolves a party id to a name on this screen, and inventing "Customer 77" would
   * read as a customer called that.
   */
  partyName: string | null;
  onSelect: (partyId: string, name: string) => void;
  onClear: () => void;
}): React.JSX.Element {
  const [term, setTerm] = useState('');
  const search = usePartyLookup(countryCode as CountryCode, term, partyFilter === null);
  const results = search.data?.data ?? [];
  const searched = term.trim().length >= 2;

  if (partyFilter !== null) {
    return (
      <div
        data-testid="customer-filter-active"
        className="flex flex-wrap items-center gap-2 rounded-sm bg-subtle px-3 py-2 text-body13 text-fg"
      >
        <span>Showing one customer only:</span>
        <span className="font-medium">
          {partyName ?? 'the customer this link names'}
        </span>
        <button
          type="button"
          data-testid="customer-filter-clear"
          onClick={onClear}
          className="inline-flex items-center gap-1 font-medium underline underline-offset-2"
        >
          <X aria-hidden="true" className="size-3.5" />
          Show all of {countryCode}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1" data-testid="customer-filter">
      <label className="flex items-center gap-2 rounded-sm border border-line bg-surface px-3 py-2">
        <Search aria-hidden="true" className="size-4 text-fg-secondary" />
        <span className="sr-only">Narrow this list to one customer</span>
        <input
          type="search"
          data-testid="customer-filter-input"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Narrow to one customer — type at least two letters"
          className="min-w-0 flex-1 bg-transparent text-body13 text-fg outline-none placeholder:text-fg-secondary"
        />
      </label>
      {/*
        ⚠ THE THREE STATES ARE ALL PRINTED. "Nothing yet", "searching" and "no such customer"
        lead to different next actions, and an empty space under a search box is read as the
        last one — which would send somebody away believing a customer is not in the system.
      */}
      {!searched ? null : search.isPending ? (
        <p className="text-body13 text-fg-secondary">Searching {countryCode}…</p>
      ) : results.length === 0 ? (
        <p className="text-body13 text-fg-secondary" data-testid="customer-filter-empty">
          No customer in {countryCode} matches “{term.trim()}”. This searches customers, not
          reminders — a customer with no reminders still appears here.
        </p>
      ) : (
        <ul className="flex flex-col overflow-hidden rounded-sm border border-line bg-surface">
          {results.map((party) => (
            <li key={party.id} className="border-b border-line last:border-b-0">
              <button
                type="button"
                data-testid="customer-filter-result"
                data-party-id={party.id}
                onClick={() => {
                  onSelect(party.id, party.name);
                  setTerm('');
                }}
                className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-subtle"
              >
                <span className="text-body13 font-medium text-fg">{party.name}</span>
                {party.city === null ? null : (
                  <span className="text-label text-fg-secondary">{party.city}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
